import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../allauth/auth";
import { USER_TYPE } from "../object-actions/types/types";
import { useAppContext } from "./AppContext";

interface RoleProtectedRouteProps {
    children: React.ReactNode;
    requiredRoles: USER_TYPE[];
    requireAll?: boolean;
}

export function RoleProtectedRoute({
    children,
    requiredRoles,
    requireAll = false
}: RoleProtectedRouteProps) {
    const location = useLocation();
    const auth = useAuth();
    const { membership } = useAppContext();

    // Check if user is authenticated
    if (!auth?.data?.user) {
        const next = `next=${encodeURIComponent(location.pathname + location.search)}`;
        return <Navigate to={`/account/login?${next}`} />;
    }

    // Check if user has any of the required roles in their membership
    const availableRoles = membership?.roles || [];
    const hasRequiredRole = requireAll
        ? requiredRoles.every(role => availableRoles.includes(role))
        : requiredRoles.some(role => availableRoles.includes(role));

    if (!hasRequiredRole) {
        // User doesn't have any of the required roles
        return (
            <Navigate
                to="/"
                state={{
                    error: `Access denied. This page requires ${requiredRoles.join(' or ')} access.`
                }}
            />
        );
    }

    // User has the required role, render the protected content
    return <>{children}</>;
}
